<script setup lang="ts">
/**
 * Gallery — saved sketches (brief §18). Local-only, unlimited on free tier.
 */
import { onMounted, ref } from 'vue';
import { IconCheck, IconPencil, IconTrash } from '@tabler/icons-vue';
import { useSketchbookStore } from '@/stores/sketchbook';
import { usePatternStore } from '@/stores/pattern';
import { useToolsStore } from '@/stores/tools';
import { useUiStore } from '@/stores/ui';
import { getWallpaperGroup } from '@/patterns/wallpaper';

const sketchbook = useSketchbookStore();
const patternStore = usePatternStore();
const tools = useToolsStore();
const ui = useUiStore();

const editingId = ref<string | null>(null);
const nameDraft = ref('');
const confirmDeleteId = ref<string | null>(null);

// ── multi-select delete ──────────────────────────────────────────────────────
const selectMode = ref(false);
const selectedIds = ref<Set<string>>(new Set());
const confirmMulti = ref(false);

function toggleSelectMode(): void {
  selectMode.value = !selectMode.value;
  selectedIds.value = new Set();
}

function toggleSelected(id: string): void {
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
}

function onCardActivate(id: string): void {
  if (selectMode.value) toggleSelected(id);
  else void openSketch(id);
}

async function deleteSelected(): Promise<void> {
  await sketchbook.deleteMany([...selectedIds.value]);
  selectedIds.value = new Set();
  selectMode.value = false;
  confirmMulti.value = false;
}

const vFocus = {
  mounted: (el: HTMLInputElement) => {
    el.focus();
    el.select();
  },
};

function startRename(id: string, current: string): void {
  editingId.value = id;
  nameDraft.value = current;
}

async function commitRename(id: string): Promise<void> {
  if (editingId.value !== id) return;
  editingId.value = null;
  const name = nameDraft.value.trim();
  const meta = sketchbook.sketches.find((m) => m.id === id);
  if (!name || !meta || name === meta.name) return;
  await sketchbook.renameSketch(id, name);
}

async function confirmDelete(): Promise<void> {
  if (!confirmDeleteId.value) return;
  await sketchbook.deleteSketch(confirmDeleteId.value);
  confirmDeleteId.value = null;
}

onMounted(() => {
  void sketchbook.refresh();
});

function close(): void {
  ui.showGallery = false;
}

async function openSketch(id: string): Promise<void> {
  await sketchbook.openSketch(id);
  const meta = sketchbook.sketches.find((m) => m.id === id);
  // Existing work is never blocked — restore its wallpaper group directly.
  if (meta) {
    patternStore.applyWpg(meta.variantId, meta.artistTile, meta.latticeParams);
    // After applyWpg: setColourCount validates against the now-current group.
    tools.setColourCount(meta.colourCount ?? 1);
    // Direct assignment, not selectColour() — opening a sketch shouldn't
    // itself flip sketchColourActive for one that was never coloured.
    tools.baseColourIndex = meta.baseColourIndex ?? 0;
  }
  close();
}

async function newSketch(): Promise<void> {
  await sketchbook.newSketch(patternStore.currentPatternId, patternStore.currentVariantId);
  close();
}

function patternName(id: string): string {
  try {
    return getWallpaperGroup(id as never).label;
  } catch {
    return id;
  }
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
</script>

<template>
  <div v-if="ui.showGallery" class="sheet-backdrop" @click.self="close">
    <div class="sheet" role="dialog" aria-modal="true">
      <header>
        <h2>Sketches</h2>
        <div class="header-actions">
          <template v-if="selectMode">
            <button class="link-action" @click="toggleSelectMode">Cancel</button>
            <button
              class="new-btn danger"
              :disabled="selectedIds.size === 0"
              @click="confirmMulti = true"
            >
              Delete ({{ selectedIds.size }})
            </button>
          </template>
          <template v-else>
            <button v-if="sketchbook.sketches.length" class="link-action" @click="toggleSelectMode">
              Select
            </button>
            <button class="new-btn" @click="newSketch">+ New</button>
          </template>
        </div>
      </header>

      <p v-if="sketchbook.sketches.length === 0" class="empty">
        Nothing saved yet — your sketches autosave as you draw.
      </p>

      <ul v-else class="grid">
        <li v-for="meta in sketchbook.sketches" :key="meta.id">
          <div
            class="card"
            :class="{
              current: meta.id === sketchbook.currentSketchId,
              selected: selectMode && selectedIds.has(meta.id),
            }"
            role="button"
            tabindex="0"
            @click="onCardActivate(meta.id)"
            @keydown.enter.self="onCardActivate(meta.id)"
          >
            <span v-if="selectMode" class="select-check" :class="{ on: selectedIds.has(meta.id) }">
              <IconCheck v-if="selectedIds.has(meta.id)" :size="13" stroke-width="3" />
            </span>
            <img v-if="meta.thumbnail" :src="meta.thumbnail" alt="" />
            <div v-else class="thumb-fallback">{{ patternName(meta.patternId) }}</div>
            <input
              v-if="editingId === meta.id"
              v-model="nameDraft"
              v-focus
              class="name-input"
              type="text"
              maxlength="60"
              spellcheck="false"
              :aria-label="`Rename ${meta.name}`"
              @click.stop
              @keydown.enter.stop="commitRename(meta.id)"
              @keydown.escape.stop="editingId = null"
              @blur="commitRename(meta.id)"
            />
            <span v-else class="card-name">{{ meta.name }}</span>
            <span class="card-meta">{{ patternName(meta.patternId) }} · {{ formatDate(meta.updatedAt) }}</span>
          </div>
          <button
            v-if="!selectMode"
            class="corner-btn rename-btn"
            :aria-label="`Rename ${meta.name}`"
            @click.stop="startRename(meta.id, meta.name)"
          >
            <IconPencil :size="16" stroke-width="2" />
          </button>
          <button
            v-if="!selectMode"
            class="corner-btn delete-btn"
            :aria-label="`Delete ${meta.name}`"
            @click="confirmDeleteId = meta.id"
          >
            <IconTrash :size="16" stroke-width="2" />
          </button>
        </li>
      </ul>

      <button class="link-btn" @click="close">Close</button>

      <div v-if="confirmDeleteId" class="confirm-backdrop" @click.self="confirmDeleteId = null">
        <div class="confirm-box" role="alertdialog" aria-modal="true" aria-label="Delete sketch">
          <p>
            Delete
            <strong>{{ sketchbook.sketches.find((m) => m.id === confirmDeleteId)?.name }}</strong>?
            This can't be undone.
          </p>
          <div class="confirm-actions">
            <button class="cancel-btn" @click="confirmDeleteId = null">Keep it</button>
            <button class="danger-btn" @click="confirmDelete">
              <IconTrash :size="14" stroke-width="2" />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div v-if="confirmMulti" class="confirm-backdrop" @click.self="confirmMulti = false">
        <div class="confirm-box" role="alertdialog" aria-modal="true" aria-label="Delete sketches">
          <p>
            Delete <strong>{{ selectedIds.size }}</strong>
            sketch{{ selectedIds.size === 1 ? '' : 'es' }}? This can't be undone.
          </p>
          <div class="confirm-actions">
            <button class="cancel-btn" @click="confirmMulti = false">Keep them</button>
            <button class="danger-btn" @click="deleteSelected">
              <IconTrash :size="14" stroke-width="2" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 8, 16, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.sheet {
  width: min(560px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 20px;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

h2 {
  margin: 0;
  font-size: 18px;
}

.new-btn {
  min-height: 36px;
  padding: 4px 14px;
  background: var(--accent);
  color: #0f0f1a;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.new-btn.danger {
  background: var(--danger);
  color: var(--canvas-bg);
}

.new-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.link-action {
  min-height: 36px;
  padding: 4px 8px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 13px;
  cursor: pointer;
}

.link-action:hover {
  color: var(--text-primary);
}

.card.selected {
  border-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 14%, var(--canvas-bg));
}

.select-check {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid var(--accent);
  background: var(--ui-surface);
  color: var(--accent-contrast);
}

.select-check.on {
  background: var(--accent);
}

.empty {
  color: var(--text-muted);
  font-size: 13px;
}

.grid {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}

.grid li {
  position: relative;
}

.card {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: var(--canvas-bg);
  border: 1px solid var(--ui-border);
  border-radius: 8px;
  cursor: pointer;
  color: var(--text-primary);
  text-align: left;
}

.card.current {
  border-color: var(--accent);
}

.card img,
.thumb-fallback {
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 4px;
  object-fit: cover;
}

.thumb-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--tile-fill-active);
  color: var(--text-muted);
  font-family: var(--font-mono);
  font-size: 11px;
}

.card-name {
  font-size: 13px;
}

.name-input {
  width: 100%;
  min-height: 24px;
  padding: 1px 5px;
  font-size: 13px;
  color: var(--text-primary);
  background: var(--ui-surface);
  border: 1px solid var(--accent);
  border-radius: 4px;
}

.name-input:focus {
  outline: none;
}

.card-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-muted);
}

.corner-btn {
  position: absolute;
  top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 15, 26, 0.82);
  color: var(--text-muted);
  cursor: pointer;
}

.corner-btn:hover {
  color: var(--text-primary);
}

.delete-btn {
  right: 6px;
}

.rename-btn {
  right: 42px;
}

.confirm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 8, 16, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 120;
}

.confirm-box {
  width: min(320px, calc(100vw - 48px));
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 18px;
}

.confirm-box p {
  margin: 0 0 14px;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--text-primary);
}

.confirm-actions {
  display: flex;
  gap: 8px;
}

.confirm-actions button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 38px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
}

.cancel-btn {
  background: transparent;
  color: var(--text-primary);
  border: 1px solid var(--ui-border);
}

.danger-btn {
  background: var(--danger);
  color: var(--canvas-bg);
  border: none;
}

.link-btn {
  display: block;
  width: 100%;
  min-height: 36px;
  background: transparent;
  color: var(--text-muted);
  border: none;
  cursor: pointer;
}
</style>
